/**
 * Date Conversion Utilities for Gregorian (AD) and Bikram Sambat (BS) Calendars
 *
 * This module provides accurate date conversion using lookup tables for Bikram Sambat calendar.
 * The BS calendar starts around April 13/14 each year and uses variable month lengths (29-32 days).
 */

export interface BSDay {
  bs_y: number;
  bs_m: number;
  bs_d: number;
}

export interface ADDay {
  ad_y: number;
  ad_m: number;
  ad_d: number;
}

export interface CurrentDate {
  year: number;
  month: number;
  day: number;
}

export interface MonthInfo {
  monthName: string;
  eng_month_index: number;
  nep_month_index: number;
  totalDaysInBsMonth: number;
  ad_month: string;
  totalDaysInAdMonth: number;
}

// Lookup table for BS years 1978-2099
// Format: [year, days_in_month_1, ..., days_in_month_12]
const BS: number[][] = [];
BS[1978] = [1978, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[1979] = [1979, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[1980] = [1980, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[1981] = [1981, 31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
BS[1982] = [1982, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[1983] = [1983, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[1984] = [1984, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[1985] = [1985, 31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
BS[1986] = [1986, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[1987] = [1987, 31, 32, 31, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[1988] = [1988, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[1989] = [1989, 31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
BS[1990] = [1990, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[1991] = [1991, 31, 32, 31, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[1992] = [1992, 31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[1993] = [1993, 31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
BS[1994] = [1994, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[1995] = [1995, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
BS[1996] = [1996, 31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[1997] = [1997, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[1998] = [1998, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[1999] = [1999, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2000] = [2000, 30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[2001] = [2001, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2002] = [2002, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2003] = [2003, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2004] = [2004, 30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[2005] = [2005, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2006] = [2006, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2007] = [2007, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2008] = [2008, 31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 29, 31];
BS[2009] = [2009, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2010] = [2010, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2011] = [2011, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2012] = [2012, 31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
BS[2013] = [2013, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2014] = [2014, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2015] = [2015, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2016] = [2016, 31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
BS[2017] = [2017, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2018] = [2018, 31, 32, 31, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2019] = [2019, 31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[2020] = [2020, 31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2021] = [2021, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2022] = [2022, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
BS[2023] = [2023, 31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[2024] = [2024, 31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2025] = [2025, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2026] = [2026, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2027] = [2027, 30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[2028] = [2028, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2029] = [2029, 31, 31, 32, 31, 32, 30, 30, 29, 30, 29, 30, 30];
BS[2030] = [2030, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2031] = [2031, 30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[2032] = [2032, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2033] = [2033, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2034] = [2034, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2035] = [2035, 30, 32, 31, 32, 31, 31, 29, 30, 30, 29, 29, 31];
BS[2036] = [2036, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2037] = [2037, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2038] = [2038, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2039] = [2039, 31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
BS[2040] = [2040, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2041] = [2041, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2042] = [2042, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2043] = [2043, 31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
BS[2044] = [2044, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2045] = [2045, 31, 32, 31, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2046] = [2046, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2047] = [2047, 31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2048] = [2048, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2049] = [2049, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
BS[2050] = [2050, 31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[2051] = [2051, 31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2052] = [2052, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2053] = [2053, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
BS[2054] = [2054, 31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[2055] = [2055, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2056] = [2056, 31, 31, 32, 31, 32, 30, 30, 29, 30, 29, 30, 30];
BS[2057] = [2057, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2058] = [2058, 30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[2059] = [2059, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2060] = [2060, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2061] = [2061, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2062] = [2062, 30, 32, 31, 32, 31, 31, 29, 30, 29, 30, 29, 31];
BS[2063] = [2063, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2064] = [2064, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2065] = [2065, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2066] = [2066, 31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 29, 31];
BS[2067] = [2067, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2068] = [2068, 31, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2069] = [2069, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2070] = [2070, 31, 31, 31, 32, 31, 31, 29, 30, 30, 29, 30, 30];
BS[2071] = [2071, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2072] = [2072, 31, 32, 31, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2073] = [2073, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];
BS[2074] = [2074, 31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2075] = [2075, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2076] = [2076, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
BS[2077] = [2077, 31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[2078] = [2078, 31, 31, 31, 32, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2079] = [2079, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2080] = [2080, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 30];
BS[2081] = [2081, 31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 29, 31];
BS[2082] = [2082, 31, 31, 32, 31, 31, 31, 30, 29, 30, 29, 30, 30];
BS[2083] = [2083, 31, 31, 32, 31, 31, 30, 30, 30, 29, 30, 30, 30];
BS[2084] = [2084, 31, 31, 32, 31, 31, 30, 30, 30, 29, 30, 30, 30];
BS[2085] = [2085, 31, 32, 31, 32, 30, 31, 30, 30, 29, 30, 30, 30];
BS[2086] = [2086, 30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30];
BS[2087] = [2087, 31, 31, 32, 31, 31, 31, 30, 30, 29, 30, 30, 30];
BS[2088] = [2088, 30, 31, 32, 32, 30, 31, 30, 30, 29, 30, 30, 30];
BS[2089] = [2089, 30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30];
BS[2090] = [2090, 30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30];
BS[2091] = [2091, 31, 31, 32, 31, 31, 31, 30, 30, 29, 30, 30, 30];
BS[2092] = [2092, 30, 31, 32, 32, 31, 30, 30, 30, 29, 30, 30, 30];
BS[2093] = [2093, 30, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30];
BS[2094] = [2094, 31, 31, 32, 31, 31, 30, 30, 30, 29, 30, 30, 30];
BS[2095] = [2095, 31, 31, 32, 31, 31, 31, 30, 29, 30, 30, 30, 30];
BS[2096] = [2096, 30, 31, 32, 32, 31, 30, 30, 29, 30, 29, 30, 30];
BS[2097] = [2097, 31, 32, 31, 32, 31, 30, 30, 30, 29, 30, 30, 30];
BS[2098] = [2098, 31, 31, 32, 31, 31, 31, 29, 30, 29, 30, 29, 31];
BS[2099] = [2099, 31, 32, 31, 32, 31, 30, 30, 30, 29, 29, 30, 31];

const DATES = {
  MAX_DAY: 32,
  MIN_DAY: 1,
  MAX_MONTH: 12,
  MIN_MONTH: 1,
  YEAR_LENGTH: 4,
  MAX_YEAR_AD: 2040,
  MAX_MONTH_AD: 31,
  MIN_YEAR_BS: 1978,
  MAX_YEAR_BS: 2099,
};

const START_ENGLISH_DATE = "1921-04-13";
const START_NEPALI_DATE = "1978-01-01";

// Error messages
const Errors = {
  DATE_FORMAT_INVALID: "Invalid date format. Expected: YYYY-MM-DD",
  INVALID_DAY: "Invalid day",
  INVALID_MONTH: "Invalid month",
  INVALID_YEAR: "Invalid year",
  AD_DATE_OUT_OF_RANGE: "AD date is out of supported range",
  BS_DATE_OUT_OF_RANGE: "BS date is out of supported range",
};

/**
 * Format date as YYYY-MM-DD string
 */
function formattedDate(year: number, month: number, day: number): string {
  return `${year}-${("0" + month).slice(-2)}-${("0" + day).slice(-2)}`;
}

/**
 * Split date string into [year, month, day]
 */
function splitDate(date: string): [number, number, number] {
  const splittedDate = date.split("-");
  if (splittedDate.length !== 3) {
    throw new Error(Errors.DATE_FORMAT_INVALID);
  }
  return splittedDate.map(Number) as [number, number, number];
}

/**
 * Sanitize and validate date
 */
interface SanitizedDate {
  year: number;
  month: number;
  day: number;
}

function dateSanitizer(date: string): SanitizedDate {
  const [year, month, day] = splitDate(date);

  if (day > DATES.MAX_DAY || day < DATES.MIN_DAY || !day) {
    throw new Error(Errors.INVALID_DAY);
  }

  if (month > DATES.MAX_MONTH || month < DATES.MIN_MONTH || !month) {
    throw new Error(Errors.INVALID_MONTH);
  }

  if (!year || year.toString().length !== DATES.YEAR_LENGTH) {
    throw new Error(Errors.INVALID_YEAR);
  }

  return { year, month, day };
}

/**
 * Sanitize AD date
 */
function adDateSanitizer(date: string): string {
  const { year, month, day } = dateSanitizer(date);

  if (year > DATES.MAX_YEAR_AD || year < 1921) {
    throw new Error(Errors.AD_DATE_OUT_OF_RANGE);
  }

  if (month > 12 || month < 1) {
    throw new Error(Errors.INVALID_MONTH);
  }

  if (day > 31 || day < 1) {
    throw new Error(Errors.INVALID_DAY);
  }

  return formattedDate(year, month, day);
}

/**
 * Sanitize BS date
 */
function bsDateSanitizer(date: string): string {
  const { year, month, day } = dateSanitizer(date);

  if (year < DATES.MIN_YEAR_BS || year > DATES.MAX_YEAR_BS) {
    throw new Error(Errors.BS_DATE_OUT_OF_RANGE);
  }

  if (day > BS[year][month]) {
    throw new Error(`No ${day} day exists in this month.`);
  }

  return formattedDate(year, month, day);
}

/**
 * Evaluate English date by adding days
 */
function evaluateEnglishDate(date: string, days: number): string {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  const year = result.getFullYear();
  const month = result.getMonth() + 1;
  const day = result.getDate();
  return formattedDate(year, month, day);
}

/**
 * Evaluate Nepali date by adding days from start date
 */
function evaluateNepaliDate(days: number): string {
  const startDate = new Date(START_NEPALI_DATE);
  let totalDays = days;
  let year = DATES.MIN_YEAR_BS;

  // Find the BS year
  while (year <= DATES.MAX_YEAR_BS) {
    let daysInYear = 0;
    for (let month = DATES.MIN_MONTH; month <= DATES.MAX_MONTH; month++) {
      daysInYear += BS[year][month];
    }

    if (totalDays < daysInYear) {
      break;
    }

    totalDays -= daysInYear;
    year++;
  }

  // Find the month within the year
  let month = DATES.MIN_MONTH;
  while (month <= DATES.MAX_MONTH) {
    const daysInMonth = BS[year][month];
    if (totalDays < daysInMonth) {
      break;
    }
    totalDays -= daysInMonth;
    month++;
  }

  const day = totalDays + 1;

  return formattedDate(year, month, day);
}

/**
 * Convert AD date to BS date
 * Can accept either a date string "yyyy-mm-dd" or individual year, month, day parameters
 */
export function adToBS(
  adDateOrYear: string | number,
  adMonth?: number,
  adDay?: number
): BSDay | null {
  try {
    let adYear: number;
    let adMonthValue: number;
    let adDayValue: number;

    // Check if first parameter is a string (date string format)
    if (typeof adDateOrYear === "string") {
      const [year, month, day] = splitDate(adDateOrYear);
      adYear = year;
      adMonthValue = month;
      adDayValue = day;
    } else {
      // Use individual parameters
      if (adMonth === undefined || adDay === undefined) {
        throw new Error(
          "Month and day are required when using individual parameters"
        );
      }
      adYear = adDateOrYear;
      adMonthValue = adMonth;
      adDayValue = adDay;
    }

    const adDate = adDateSanitizer(
      formattedDate(adYear, adMonthValue, adDayValue)
    );
    const startDate = new Date(START_ENGLISH_DATE);
    const today = new Date(adDate);
    const daysDifference = Math.floor(
      (today.getTime() - startDate.getTime()) / 86400000
    );

    if (daysDifference < 0) {
      throw new Error(Errors.AD_DATE_OUT_OF_RANGE);
    }

    const bsDateString = evaluateNepaliDate(daysDifference);
    const [bs_y, bs_m, bs_d] = splitDate(bsDateString);

    return { bs_y, bs_m, bs_d };
  } catch (error) {
    console.error("Error converting AD to BS:", error);
    return null;
  }
}

/**
 * Convert BS date to AD date
 * Can accept either a date string "yyyy-mm-dd" or individual year, month, day parameters
 */
export function bsToAD(
  bsDateOrYear: string | number,
  bsMonth?: number,
  bsDay?: number
): ADDay | null {
  try {
    let bsYear: number;
    let bsMonthValue: number;
    let bsDayValue: number;

    // Check if first parameter is a string (date string format)
    if (typeof bsDateOrYear === "string") {
      const [year, month, day] = splitDate(bsDateOrYear);
      bsYear = year;
      bsMonthValue = month;
      bsDayValue = day;
    } else {
      // Use individual parameters
      if (bsMonth === undefined || bsDay === undefined) {
        throw new Error(
          "Month and day are required when using individual parameters"
        );
      }
      bsYear = bsDateOrYear;
      bsMonthValue = bsMonth;
      bsDayValue = bsDay;
    }

    const selectedDate = bsDateSanitizer(
      formattedDate(bsYear, bsMonthValue, bsDayValue)
    );
    const [year, month, day] = splitDate(selectedDate);

    let daysDiff = 0;

    for (let i = DATES.MIN_YEAR_BS; i <= year; i++) {
      if (i === year) {
        for (let j = DATES.MIN_MONTH; j < month; j++) {
          daysDiff += BS[i][j];
        }
        daysDiff += day - 1;
      } else {
        for (let j = DATES.MIN_MONTH; j <= DATES.MAX_MONTH; j++) {
          daysDiff += BS[i][j];
        }
      }
    }

    const adDateString = evaluateEnglishDate(START_ENGLISH_DATE, daysDiff);
    const [ad_y, ad_m, ad_d] = splitDate(adDateString);

    return { ad_y, ad_m, ad_d };
  } catch (error) {
    console.error("Error converting BS to AD:", error);
    return null;
  }
}

/**
 * Get current BS date
 */
export function getCurrentBSDate(): BSDay | null {
  try {
    const startDate = new Date(START_ENGLISH_DATE);
    const today = new Date();
    const daysDifference = Math.floor(
      (today.getTime() - startDate.getTime()) / 86400000
    );

    const bsDateString = evaluateNepaliDate(daysDifference);
    const [bs_y, bs_m, bs_d] = splitDate(bsDateString);

    return { bs_y, bs_m, bs_d };
  } catch (error) {
    console.error("Error getting current BS date:", error);
    return null;
  }
}

/**
 * Convert BS year to approximate AD year
 */
export function bsYearToAD(bsYear: number): number {
  return bsYear - 57;
}

/**
 * Convert AD year to approximate BS year
 */
export function adYearToBS(adYear: number): number {
  return adYear + 57;
}

/**
 * Get days in a BS month
 */
export function getDaysInBSMonth(month: number, year?: number): number {
  if (month < 1 || month > 12) {
    return 30; // Default
  }

  if (year && BS[year]) {
    return BS[year][month];
  }

  // Return approximate days if year not found
  const approximateDays = [
    31, // Baishakh
    31, // Jestha
    32, // Ashadh
    31, // Shrawan
    31, // Bhadra
    30, // Ashwin
    30, // Kartik
    29, // Mangsir
    30, // Poush
    30, // Magh
    29, // Falgun
    30, // Chaitra
  ];

  return approximateDays[month - 1];
}

/**
 * Get list of months with BS and AD information
 * @param year Optional BS year. If not provided, converts current AD year to BS year
 * @returns Array of month information objects
 */
export function monthList(year?: number): MonthInfo[] {
  // Nepali month names in order
  const nepaliMonths = [
    "बैशाख",
    "जेठ",
    "असार",
    "श्रावण",
    "भदौ",
    "आश्विन",
    "कार्तिक",
    "मंसिर",
    "पुष",
    "माघ",
    "फागुन",
    "चैत्र",
  ];

  // English month names
  const englishMonths = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Days in each AD month
  const adMonthDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // BS month to AD month mapping (where BS month starts)
  // BS month 1 (Baishakh) starts in AD month 4 (April), etc.
  const bsToAdMonthMap = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];

  // Determine BS year
  let bsYear: number;
  if (year !== undefined) {
    bsYear = year;
  } else {
    // Get actual current BS year
    const currentBS = getCurrentBSDate();
    bsYear = currentBS ? currentBS.bs_y : adYearToBS(new Date().getFullYear());
  }

  // Ensure BS year is within valid range
  if (bsYear < DATES.MIN_YEAR_BS || bsYear > DATES.MAX_YEAR_BS) {
    // Use approximate days if year is out of range
    bsYear = DATES.MIN_YEAR_BS; // Fallback to minimum year
  }

  return nepaliMonths.map((monthName, index) => {
    const nepMonthIndex = index + 1; // 1-12
    const engMonthIndex = bsToAdMonthMap[index]; // AD month where BS month starts
    const adMonthName = englishMonths[engMonthIndex - 1]; // Convert to 0-based index

    // Get days in BS month for the given year
    const totalDaysInBsMonth = BS[bsYear]
      ? BS[bsYear][nepMonthIndex]
      : getDaysInBSMonth(nepMonthIndex);

    // Get days in AD month (handle February leap year)
    let totalDaysInAdMonth = adMonthDays[engMonthIndex - 1];
    if (engMonthIndex === 2) {
      // Check if it's a leap year
      const adYear = bsYearToAD(bsYear);
      const isLeapYear =
        (adYear % 4 === 0 && adYear % 100 !== 0) || adYear % 400 === 0;
      if (isLeapYear) {
        totalDaysInAdMonth = 29;
      }
    }

    return {
      monthName,
      eng_month_index: engMonthIndex,
      nep_month_index: nepMonthIndex,
      totalDaysInBsMonth,
      ad_month: adMonthName,
      totalDaysInAdMonth,
    };
  });
}
